import React, { useMemo, useState } from 'react'
import { CollapsibleListToggle } from '../../../legacy-design-system/list'
import { css, cx } from '@emotion/css'
import { VulnerabilityPolicy } from '../index'
import { FormattedMessage } from 'react-intl'
import { getApplicationDrilldownRoute } from '../../route'
import {
  appImportanceI18n,
  ApplicationImportance
} from '../../application-importance'
import {
  metadataCss,
  paragraphTextCss
} from '../../../legacy-design-system/text'
import { CsWarningIcon } from '../../../legacy-design-system/icons'
import { theme as deprecatedTheme } from '../../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { WithTooltip } from '../../../design-system'
import { Link } from '@contrast-security-inc/design-system-typography'

export function ApplicationsCell({
  organizationID,
  rowData: {
    allApplications,
    applications = [],
    applicationImportance = [],
    valid
  },
  listDataTestID
}: {
  readonly organizationID: string
  readonly rowData: VulnerabilityPolicy
  readonly listDataTestID?: string
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const isNorthstarUI = useMemo(() => {
    return window.isEmbeddedInNS === true
  }, [])

  const listStyle = cx(
    paragraphTextCss,
    css`
      list-style: none;
      padding: 0;
      margin: 0;
    `
  )

  if (allApplications) {
    return (
      <div>
        <ul
          className={cx('vuln-management-applications-cell-list', listStyle)}
          data-testid={listDataTestID}
        >
          <li>
            <FormattedMessage id="title_all_applications">
              {(msg) => <span>{msg}</span>}
            </FormattedMessage>
          </li>
        </ul>
      </div>
    )
  } else if (!valid) {
    return (
      <div
        className={css`
          display: flex;
          flex-direction: row;
          align-items: center;
        `}
      >
        <WithTooltip
          tooltipMaxWidth="260px"
          renderTooltipContent={() => (
            <div>
              <FormattedMessage id="title_noDefined_applications">
                {(msg) => (
                  <div
                    className={css`
                      font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                    `}
                  >
                    {msg}
                  </div>
                )}
              </FormattedMessage>

              <FormattedMessage id="detail_noAppInPolicyTooltip_updatePolicy">
                {(msg) => <div>{msg}</div>}
              </FormattedMessage>
            </div>
          )}
          children={({ ref, onMouseEnter, onMouseLeave }) => (
            <span
              ref={ref}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <CsWarningIcon
                size={16}
                className={css`
                  transform: translateY(-1px);
                  cursor: pointer;
                `}
                color={theme.color.red50}
              />
            </span>
          )}
        />

        <FormattedMessage id="title_noDefined_applications">
          {(msg) => (
            <span
              className={cx(
                metadataCss,
                css`
                  margin-left: 2px;
                  color: ${theme.color.red50};
                  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                `
              )}
            >
              {msg}
            </span>
          )}
        </FormattedMessage>
      </div>
    )
  }

  const appsAndImportances: Array<{
    readonly label: string | ApplicationImportance
    readonly id?: string
    readonly isI18n?: boolean
  }> = [
    ...applicationImportance.map((importance) => ({
      label: importance,
      isI18n: true
    })),
    ...applications.map((app) => ({
      label: app.name,
      id: isNorthstarUI ? undefined : app.id,
      isI18n: false
    }))
  ]

  const visible = isExpanded
    ? appsAndImportances
    : appsAndImportances.slice(0, 5)

  return (
    <div>
      <ul
        className={cx('vuln-management-applications-cell-list', listStyle)}
        data-testid={listDataTestID}
      >
        {visible.map(({ label, id, isI18n }, appOrImportanceIndex) => (
          <li key={appOrImportanceIndex}>
            {!!id ? (
              <Link
                className={css`
                  color: black;
                  &:hover {
                    text-decoration: underline;
                    color: black;
                  }
                  &:link,
                  &:visited,
                  &:hover,
                  &:focus {
                    color: black;
                  }
                `}
                href={getApplicationDrilldownRoute(organizationID, id)}
              >
                {label}
              </Link>
            ) : isI18n ? (
              <FormattedMessage id={appImportanceI18n[label]}>
                {(msg) => <span>{msg} </span>}
              </FormattedMessage>
            ) : (
              label
            )}
          </li>
        ))}
      </ul>
      {appsAndImportances.length > 5 && (
        <CollapsibleListToggle
          isExpanded={isExpanded}
          onToggle={(_isExpanded) => {
            if (isExpanded !== _isExpanded) {
              setIsExpanded(_isExpanded)
            }
          }}
        />
      )}
    </div>
  )
}
